import { dbColumnFor } from '@/lib/waterModel';
import { evaluateTier, type ThresholdTable, type Tier } from '@/lib/thresholds';
import { waterColorTier, type ColorTiers } from '@/lib/waterColor';

export type StatusColor = 'green' | 'amber' | 'red' | 'gray';

const TIER_COLOR: Record<Tier, StatusColor> = { ok: 'green', warning: 'amber', critical: 'red' };

export const tierColor = (tier: Tier): StatusColor => TIER_COLOR[tier];

/** A pair's dot (alkalinity, Cal/Mag): grey until the farm's bands have loaded and a number exists, never a false green. */
export const pairColor = (table: ThresholdTable | undefined, tier: Tier, hasValue: boolean): StatusColor =>
  table && hasValue ? TIER_COLOR[tier] : 'gray';

/** The dot for a water colour word, or undefined (no dot at all) when the farm has not said what it means. */
export function waterColorDot(tiers: ColorTiers | undefined, color: string | null | undefined): StatusColor | undefined {
  const tier = waterColorTier(tiers, color);
  return tier ? TIER_COLOR[tier] : undefined;
}
const RANK: Record<StatusColor, number> = { gray: 0, green: 1, amber: 2, red: 3 };

/**
 * Dot colour: the entry grid's own recommended bands, so a card never shows green for a
 * value the grid flags; and the farm's alert thresholds on top, so a farm that set a
 * stricter limit still sees it. The worse of the two wins.
 */
export function paramColor(
  table: ThresholdTable | undefined,
  paramKey: string,
  value: number | null | undefined,
  farmMin?: number,
  farmMax?: number,
  farmCriticalOffset = 0.5,
): StatusColor {
  // Until the farm's table has loaded nothing can be judged: grey, never a false green.
  if (value === null || value === undefined || !table) return 'gray';
  const recommended = TIER_COLOR[evaluateTier(table, dbColumnFor(paramKey), value)];
  if (farmMin === undefined || farmMax === undefined) return recommended;
  let farm: StatusColor = 'green';
  if (value < farmMin - farmCriticalOffset || value > farmMax + farmCriticalOffset) farm = 'red';
  else if (value < farmMin || value > farmMax) farm = 'amber';
  return RANK[farm] > RANK[recommended] ? farm : recommended;
}
